import React, { useEffect, useState } from "react";
import { ordersApi } from "../services/orders";
import type { RefundPreview } from "../types/orders";
import BottomSheet from "./BottomSheet";
import { formatSingleAmount } from "../utils/currency";
import "./RequestDetailSheet.css";
import { formatWhen } from "../utils/formatWhen";
import { t } from "../i18n";
import { T } from "../i18n/T";

// Request detail sheet — opens on a TAP of a played (or refunded) row in
// Request History (founder design, locked; mockup
// in the design gallery). The refund action deliberately
// lives INSIDE this sheet, quiet, not as a button on every row: findable when
// a talent needs it, never advertised.
//
// Flow: detail → (tap "Refund this request") → confirm with EXACT per-order
// figures from GET /orders/:id/refund-preview — never estimates — or the
// honest block message when the earnings were already cashed out. Confirm →
// POST /orders/:id/refund → parent flips the row to REFUNDED.

export interface DetailSheetOrder {
  _id: string;
  searchQuery: string;
  trackArtists?: string[];
  trackImage?: string;
  amount?: number | null;
  currency?: string;
  status: string;
  playedAt?: string | null;
  refundedAt?: string | null;
  createdAt: string;
  updatedAt?: string;
  clientNickname?: string | null;
  customerName?: string | null;
  chargedToPlatform?: boolean;
}

type Preview = RefundPreview;

interface Props {
  order: DetailSheetOrder;
  onClose: () => void;
  /** The refund went through — parent flips the row + refreshes balances. */
  onRefunded: (orderId: string) => void;
}

const REFUND_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

// The block copy per server reason. balance_short / already_cashed_out /
// lookup failures share the founder-approved message — same truth either way.
const BLOCK_MESSAGES: Record<string, string> = {
  balance_short:
    t("requestDetailSheet.yourBalanceDoesnT"),
  already_cashed_out:
    t("requestDetailSheet.yourBalanceDoesnT"),
  no_stripe_account:
    t("requestDetailSheet.yourBalanceDoesnT"),
  balance_lookup_failed:
    t("requestDetailSheet.weCouldnTCheck"),
  window_expired: t("requestDetailSheet.thisRequestIsPast"),
  disputed: t("requestDetailSheet.thisPaymentIsUnder"),
  legacy_unsupported: t("requestDetailSheet.toRefundThisRequest"),
};

const RequestDetailSheet: React.FC<Props> = ({ order, onClose, onRefunded }) => {
  const [mode, setMode] = useState<"detail" | "confirm">("detail");
  const [preview, setPreview] = useState<Preview | null>(null);
  const [previewFailed, setPreviewFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [refundError, setRefundError] = useState<string | null>(null);

  const cur = order.currency || "usd";
  const refunded = order.status === "refunded";
  const playedWhen = order.playedAt || order.updatedAt || order.createdAt;
  const inWindow =
    Date.now() - new Date(order.playedAt || order.createdAt).getTime() <= REFUND_WINDOW_MS;
  const fanName = order.clientNickname || order.customerName || null;

  // The quiet link renders only where a refund is even conceivable — the
  // server re-runs every gate on tap, so this is UX, never authorization.
  const showRefundAction =
    !refunded && order.status === "played" && (order.amount ?? 0) > 0 && inWindow;

  // Exact figures load when the talent taps "Refund this request" — not on
  // sheet open. Only DJs actually heading for a refund cost a Stripe read.
  useEffect(() => {
    if (mode !== "confirm" || preview) return;
    let alive = true;
    ordersApi
      .refundPreview(order._id)
      .then((data) => {
        if (alive) setPreview(data);
      })
      .catch(() => {
        if (alive) setPreviewFailed(true);
      });
    return () => {
      alive = false;
    };
  }, [mode, preview, order._id]);

  const handleRefund = async () => {
    if (busy) return;
    setBusy(true);
    setRefundError(null);
    try {
      await ordersApi.refund(order._id);
      onRefunded(order._id);
      onClose();
    } catch (err: any) {
      const data = err?.response?.data;
      setRefundError(
        (data?.reason && BLOCK_MESSAGES[data.reason]) ||
          t("requestDetailSheet.refundFailedNothingWas")
      );
      setBusy(false);
    }
  };

  const detailBody = (
    <>
      <div className="rds-head">
        <img
          className="rds-art"
          src={order.trackImage || "/fallback.png"}
          alt=""
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).src = "/fallback.png";
          }}
        />
        <div className="rds-head-text">
          <div className="rds-title">{order.searchQuery}</div>
          <div className="rds-artist">{(order.trackArtists || []).join(", ")}</div>
        </div>
      </div>

      <div className="rds-meta">
        <div className="rds-meta-row">
          <span>{t("requestDetailSheet.requestedBy")}</span>
          <b>{fanName || t("requestDetailSheet.aFan")}</b>
        </div>
        <div className="rds-meta-row">
          <span>{t("requestDetailSheet.played")}</span>
          <b>{formatWhen(playedWhen)}</b>
        </div>
        {refunded ? (
          <>
            <div className="rds-meta-row">
              <span>{t("requestDetailSheet.refunded")}</span>
              <b>{formatWhen(order.refundedAt)}</b>
            </div>
            <div className="rds-meta-row">
              <span>{t("requestDetailSheet.earned")}</span>
              <span className="rds-amount--struck">
                {formatSingleAmount(order.amount ?? 0, cur)}
              </span>
            </div>
          </>
        ) : (
          <div className="rds-meta-row">
            <span>{t("requestDetailSheet.earned")}</span>
            <span className="rds-amount--in">+{formatSingleAmount(order.amount ?? 0, cur)}</span>
          </div>
        )}
      </div>

      {refunded && <div className="rds-refunded-chip">{t("requestDetailSheet.refunded2")}</div>}

      {showRefundAction && (
        <>
          <button type="button" className="rds-refund-link" onClick={() => setMode("confirm")}>
            {t("requestDetailSheet.refundThisRequest")}
          </button>
          <div className="rds-hint">{t("requestDetailSheet.availableForDaysAfter")}</div>
        </>
      )}
    </>
  );

  // Confirm state: loading → figures (or block). previewFailed renders the
  // retry-flavored block message rather than a dead sheet.
  const blockReason = previewFailed
    ? "balance_lookup_failed"
    : preview && !preview.refundable
    ? preview.reason || "balance_lookup_failed"
    : null;

  const confirmBody = !preview && !previewFailed ? (
    <p className="rds-loading">{t("requestDetailSheet.checkingThisRefund")}</p>
  ) : blockReason ? (
    <>
      <div className="rds-block">{BLOCK_MESSAGES[blockReason] || BLOCK_MESSAGES.balance_lookup_failed}</div>
      <button type="button" className="rds-btn rds-btn--cancel" onClick={onClose}>
        {t("requestDetailSheet.close")}
      </button>
    </>
  ) : (
    <>
      <div className="rds-confirm-copy">
        <b>
          {fanName ? t("requestDetailSheet.fanGetsFullBack", { fanName, amount: formatSingleAmount(preview!.fanTotal ?? 0, preview!.currency || cur) }) : t("requestDetailSheet.yourFanGetsFullBack", { amount: formatSingleAmount(preview!.fanTotal ?? 0, preview!.currency || cur) })}
        </b>
        <br />
        <T k="requestDetailSheet.yourEarningsDrop" vars={{ amount: formatSingleAmount(preview!.djDrop ?? 0, preview!.currency || cur) }} tags={{ 0: (c) => <span className="rds-drop">{c}</span> }} />
        {typeof preview!.cardFee === "number" && preview!.cardFee > 0 && (
          <>
            <br />{t("requestDetailSheet.aCardFeeComes", { amount: formatSingleAmount(preview!.cardFee, preview!.currency || cur) })}
          </>
        )}
      </div>
      {refundError && <div className="rds-block">{refundError}</div>}
      <button
        type="button"
        className="rds-btn rds-btn--refund"
        disabled={busy}
        onClick={handleRefund}
      >
        {busy
          ? t("requestDetailSheet.refunding")
          : t("requestDetailSheet.refund", { amount: formatSingleAmount(preview!.fanTotal ?? 0, preview!.currency || cur) })}
      </button>
      <button type="button" className="rds-btn rds-btn--cancel" disabled={busy} onClick={onClose}>
        {t("requestDetailSheet.cancel")}
      </button>
    </>
  );

  return (
    <BottomSheet
      open
      title={mode === "confirm" ? t("requestDetailSheet.refundThisRequest2") : t("requestDetailSheet.request")}
      onClose={onClose}
    >
      <div className="rds-body">{mode === "confirm" ? confirmBody : detailBody}</div>
    </BottomSheet>
  );
};

export default RequestDetailSheet;
